import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { useViewList } from "@/context/ViewListContext";
import { useStartCourseJob } from "@/hooks/api/course/useStartCourseJob";
import { useEffect, useState, useRef } from "react";
import { useSaveCourse } from "@/hooks/api/course/useSaveCourse";
import { CourseCreatedStep } from "@/partials/create/course/CourseCreatedStep";
import { CourseProgressStep } from "@/partials/create/course/CourseProgressStep/CourseProgressStep";
import { CreateCharacterStep } from "@/partials/create/course/CreateCharacterStep/CreateCharacterStep";
import {
  UploadEbookFormValues,
  UploadEbookStep,
} from "@/partials/create/course/ebook/UploadEbookStep";
import { CourseReviewStep } from "@/partials/create/course/CourseReviewStep/CourseReviewStep";
import { EbookStepper } from "@/partials/create/course/ebook/EbookStepper";
import { isDev } from "@/mocks";
import { Course, COURSE_TYPE, CourseData } from "@vixi/types";
import { RouteOptionsById, RouterIds, router, useNavigate } from "@vixi/router";
import { CourseEditorContextProvider, useUiStore } from "@vixi/ui";
import { logger, courseGenerationToCourse, forEachLesson } from "@vixi/utils";
import { useCourseJobStatus } from "@/hooks/api/course/useCourseJobStatus";

const JOB_ID_STORAGE_KEY = "course-generation-job-id";
const JOB_COURSE_DATA_STORAGE_KEY = "course-generation-job-course-data"; // Store course data (eBookAbout, targetAudience, conversationTone) with jobId
const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id"; // Temporary jobId saved when user clicks on card
const routeId = "/_app/create/ebook/flow" satisfies RouterIds;
const Route = router.routesById[routeId];

// Constants
const firstStep = 0;
const progressStep = 1; // Step for progress page
const createCharacterStep = 2; // Step for Create Character (after progress)

export const CreateEbookCourseFlowPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
};

function RouteComponent() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  
  // Check for jobId in URL or localStorage (for refresh handling)
  const [jobId, setJobId] = useState<string | null>(
    search.jobId || localStorage.getItem(JOB_ID_STORAGE_KEY)
  );

  // Check if we should skip to CreateCharacterStep (when progress = 100% and completed)
  // This happens when user clicks Continue on a completed job (progress = 100%)
  const shouldSkipToCharacter = search.skipToCharacter === "true";

  // useGoBack used inside of children components. Not needed here
  // If skipToCharacter is true, start at CreateCharacterStep (step 1 after removing progress step)
  // If jobId exists, start at progress step
  // Otherwise, start at first step (UploadEbookStep)
  const stepperFlow = useViewList({
    initialStep: shouldSkipToCharacter ? 1 : (jobId ? progressStep : firstStep), // CreateCharacterStep is at index 1 when jobId is cleared
  });
  const { activeStep, nextStep, goToStep } = stepperFlow;
  // TODO: Use mutation state to handle previous values (mutationState.variables)
  const [prevValue, setPrevValue] = useState<UploadEbookFormValues | null>(null);

  // Stepper
  const {
    header: { setChildren: setHeaderChildren },
    sidebar: { setDisabled: setSidebarDisabled },
  } = useUiStore();

  useEffect(() => {
    setHeaderChildren(<EbookStepper activeStep={activeStep} />);
    setSidebarDisabled(() => true);

    return () => {
      setHeaderChildren(null);
      setSidebarDisabled(() => false);
    };
  }, [activeStep]);

  // Save Course
  const saveCourseMutation = useSaveCourse();
  const { data: courseId } = saveCourseMutation;

  // Store
  const [courseData, setCourseData] = useState<Course | null | undefined>();
  useEffect(() => {
    setCourseData({
      courseType: COURSE_TYPE.EBOOK,
      chapters: [],
    });

    return () => {
      setCourseData({});
      // Clear temp active jobId when component unmounts (user navigates away without saving)
      try {
        localStorage.removeItem(TEMP_ACTIVE_JOB_ID_KEY);
        logger.info("🗑️ Cleared temp active jobId on unmount");
      } catch (error) {
        logger.error("❌ Failed to clear temp active jobId:", error);
      }
    };
  }, []);

  // Handle course generation - Start async job
  const startJobMutation = useStartCourseJob();
  const { mutateAsync: startJob, isSuccess: isJobStarted, isPending: isStartingJob } = startJobMutation;

  const onUploadCourse = async (values: UploadEbookFormValues) => {
    logger.info("📝 onUploadCourse called with values:", {
      hasEbook: !!values.ebook?.length,
      hasBookInfo: !!values.bookInfo,
      about: values.bookInfo?.about || "MISSING",
      audience: values.bookInfo?.audience || "MISSING",
      tone: values.bookInfo?.tone || "MISSING",
      lang: values.bookInfo?.lang || "MISSING",
      fullValues: values,
    });
    
    setPrevValue(values);
    
    try {
      logger.info("🚀 Starting course generation job via POST /api/CourseMaker/async");
      
      // Validate that form values exist before starting job
      if (!values.bookInfo?.about || !values.bookInfo?.audience || !values.bookInfo?.tone) {
        logger.error("❌ Missing form values in onUploadCourse:", {
          about: !!values.bookInfo?.about,
          audience: !!values.bookInfo?.audience,
          tone: !!values.bookInfo?.tone,
          fullValues: values,
        });
        alert("Please fill in all course details (About, Target Audience, Conversation Tone) before creating the course.");
        return;
      }
      
      // Start the async job - this calls POST /api/CourseMaker/async
      // Response will contain ONLY jobId, NOT course data
      const jobResponse = await startJob({
        lang: values.bookInfo.lang,
        pdfFiles: values.ebook,
      });

      logger.info("✅ Job started successfully. Received jobId:", jobResponse.jobId);
      logger.info("📦 Full response:", jobResponse);

      if (jobResponse.jobId) {
        // Save jobId to localStorage for refresh recovery
        localStorage.setItem(JOB_ID_STORAGE_KEY, jobResponse.jobId);
        logger.info("💾 Saved jobId to localStorage:", jobResponse.jobId);
        
        // Update URL with jobId so we can recover on refresh
        const url = new URL(window.location.href);
        url.searchParams.set("jobId", jobResponse.jobId);
        window.history.pushState({}, "", url.toString());
        logger.info("🔗 Updated URL with jobId:", url.toString());

        // Set jobId state
        setJobId(jobResponse.jobId);

        // Move to progress step to show progress polling
        if (activeStep === 0) {
          nextStep();
        }

        // Save form values to course data
        // Validate that form values exist before saving
        if (!values.bookInfo?.about || !values.bookInfo?.audience || !values.bookInfo?.tone) {
          logger.error("❌ Missing form values:", {
            about: !!values.bookInfo?.about,
            audience: !!values.bookInfo?.audience,
            tone: !!values.bookInfo?.tone,
            fullValues: values,
          });
          alert("Please fill in all course details (About, Target Audience, Conversation Tone) before creating the course.");
          return;
        }
        
        // Extract and validate metadata from form values
        const courseMetadata = {
          conversationTone: values.bookInfo?.tone || "",
          targetAudience: values.bookInfo?.audience || "",
          eBookAbout: values.bookInfo?.about || "",
        };
        
        // Double-check that we have all required fields
        if (!courseMetadata.conversationTone || !courseMetadata.targetAudience || !courseMetadata.eBookAbout) {
          logger.error("❌ Missing metadata fields after extraction:", {
            conversationTone: !!courseMetadata.conversationTone,
            targetAudience: !!courseMetadata.targetAudience,
            eBookAbout: !!courseMetadata.eBookAbout,
            originalValues: values,
          });
          alert("Please fill in all course details (About, Target Audience, Conversation Tone) before creating the course.");
          return;
        }
        
        logger.info("💾 Saving course metadata:", {
          conversationTone: courseMetadata.conversationTone.substring(0, 50),
          targetAudience: courseMetadata.targetAudience.substring(0, 50),
          eBookAbout: courseMetadata.eBookAbout.substring(0, 50),
          jobId: jobResponse.jobId,
        });
        
        // Use functional update to ensure we have the latest state
        setCourseData((prev) => {
          const updated = {
            ...prev,
            courseType: prev?.courseType ?? COURSE_TYPE.EBOOK,
            chapters: prev?.chapters ?? [],
            ...courseMetadata,
          };
          logger.info("💾 Updated courseData state with metadata:", {
            hasMetadata: !!(updated.eBookAbout && updated.targetAudience && updated.conversationTone),
            eBookAbout: updated.eBookAbout?.substring(0, 30) || "MISSING",
            targetAudience: updated.targetAudience?.substring(0, 30) || "MISSING",
            conversationTone: updated.conversationTone?.substring(0, 30) || "MISSING",
          });
          return updated;
        });
        
        // TEMPORARY SOLUTION - Backend TODO Required:
        // ===========================================
        // Save course metadata with jobId in localStorage for matching with saved courses.
        // This is a workaround until backend adds courseId to job response.
        //
        // BACKEND TODO: Add courseId field to GET /api/CourseMaker/jobs/{jobId} response
        //
        // Expected response format:
        // {
        //   "jobId": "abc123",
        //   "courseId": 123,  // <-- ADD THIS FIELD
        //   "status": 2,
        //   "progressPercentage": 100,
        //   ...
        // }
        //
        // Once courseId is available:
        // - We can directly check if course exists in database by courseId
        // - We can eliminate localStorage matching logic
        // - Card visibility logic will be simpler and more reliable
        try {
          const jobCourseData = {
            jobId: jobResponse.jobId,
            eBookAbout: courseMetadata.eBookAbout,
            targetAudience: courseMetadata.targetAudience,
            conversationTone: courseMetadata.conversationTone,
          };
          
          const storageKey = `${JOB_COURSE_DATA_STORAGE_KEY}-${jobResponse.jobId}`;
          const dataToStore = JSON.stringify(jobCourseData);
          
          logger.info("💾 Preparing to save to localStorage:", {
            key: storageKey,
            dataLength: dataToStore.length,
            hasEBookAbout: !!jobCourseData.eBookAbout,
            hasTargetAudience: !!jobCourseData.targetAudience,
            hasConversationTone: !!jobCourseData.conversationTone,
            dataPreview: {
              eBookAbout: jobCourseData.eBookAbout?.substring(0, 30),
              targetAudience: jobCourseData.targetAudience?.substring(0, 30),
              conversationTone: jobCourseData.conversationTone?.substring(0, 30),
            },
          });
          
          localStorage.setItem(storageKey, dataToStore);
          
          // Verify it was saved correctly
          const verify = localStorage.getItem(storageKey);
          if (verify) {
            try {
              const parsed = JSON.parse(verify);
              logger.info("✅ Verified metadata saved successfully:", {
                key: storageKey,
                hasEBookAbout: !!parsed.eBookAbout,
                hasTargetAudience: !!parsed.targetAudience,
                hasConversationTone: !!parsed.conversationTone,
                eBookAbout: parsed.eBookAbout?.substring(0, 30) || "MISSING",
                targetAudience: parsed.targetAudience?.substring(0, 30) || "MISSING",
                conversationTone: parsed.conversationTone?.substring(0, 30) || "MISSING",
              });
            } catch (parseError) {
              logger.error("❌ Failed to parse verified data:", parseError, "Raw data:", verify);
            }
          } else {
            logger.error("❌ Failed to verify metadata save - localStorage.getItem returned null");
            // Try to check if localStorage is available
            try {
              const testKey = "test-localStorage-write";
              localStorage.setItem(testKey, "test");
              const testValue = localStorage.getItem(testKey);
              localStorage.removeItem(testKey);
              if (!testValue) {
                logger.error("❌ localStorage appears to be disabled or full");
              }
            } catch (testError) {
              logger.error("❌ localStorage test failed:", testError);
            }
          }
        } catch (error) {
          logger.error("❌ Failed to save course metadata:", error);
          // Show user-friendly error
          alert("Failed to save course information. Please try again.");
        }
      } else {
        logger.error("❌ No jobId received in response:", jobResponse);
      }
    } catch (error) {
      logger.error("❌ Failed to start job", error);
    }
  };

  // Handle course completion from progress step
  const onCourseComplete = (generatedCourse: CourseData.Course) => {
    logger.log("Course generation completed: ", generatedCourse);
    
    // IMPORTANT: Keep jobId and metadata in localStorage until course is saved
    // Don't clear jobId here - we need it to identify which card to remove when saving
    // Only clear jobId from URL to hide progress step
    // Note: We don't clear course metadata here because we need it to match with saved courses
    // It will be cleared when course is confirmed saved (In Review or Published)
    
    // Update course data with generated course
    // IMPORTANT: Preserve existing metadata (eBookAbout, targetAudience, conversationTone) from prev
    // Also try to load metadata from localStorage if missing
    // Using functional update to ensure we have the latest state
    setCourseData((prev) => {
      // Try to load metadata from localStorage if missing
      let metadataFromStorage: { eBookAbout?: string; targetAudience?: string; conversationTone?: string } = {};
      if (jobId) {
        try {
          const storedData = localStorage.getItem(`${JOB_COURSE_DATA_STORAGE_KEY}-${jobId}`);
          if (storedData) {
            const parsed = JSON.parse(storedData);
            metadataFromStorage = {
              eBookAbout: parsed.eBookAbout,
              targetAudience: parsed.targetAudience,
              conversationTone: parsed.conversationTone,
            };
            logger.info("📦 Loaded metadata from localStorage in onCourseComplete:", metadataFromStorage);
          }
        } catch (error) {
          logger.error("❌ Failed to load metadata from localStorage:", error);
        }
      }
      
      const updated = {
        ...prev,
        chapters: generatedCourse.chapters ?? [],
        // Preserve metadata from prev, or use metadata from localStorage, or keep undefined
        eBookAbout: prev?.eBookAbout || metadataFromStorage.eBookAbout,
        targetAudience: prev?.targetAudience || metadataFromStorage.targetAudience,
        conversationTone: prev?.conversationTone || metadataFromStorage.conversationTone,
        courseType: prev?.courseType ?? COURSE_TYPE.EBOOK,
      };
      logger.info("✅ Updated courseData with chapters:", updated.chapters?.length);
      logger.info("✅ Preserved metadata:", {
        eBookAbout: !!updated.eBookAbout,
        targetAudience: !!updated.targetAudience,
        conversationTone: !!updated.conversationTone,
      });
      return updated;
    });
  };

  // Fetch job status to check if we need to load course data immediately
  // This handles the case when user clicks on card with progress = 100%
  const { data: jobStatus, isError: isJobStatusError, error: jobStatusError } = useCourseJobStatus(jobId, !!jobId);
  const hasLoadedDataFromResult = useRef(false);

  // Clear invalid jobId (e.g., from different user account)
  // If jobId doesn't exist in API (404 or 401), clear it and start from first step
  // But if user logs in with same account, jobId should still work
  // IMPORTANT: Only remove metadata if jobId is unauthorized (401) - not if just not found (404)
  // because 404 might mean job is still processing or API hasn't loaded yet
  useEffect(() => {
    if (jobId && isJobStatusError) {
      const errorStatus = (jobStatusError as any)?.response?.status;
      // Only clear if unauthorized (401) - this means job belongs to different user
      // Don't clear on 404 - job might still exist but API hasn't loaded yet
      if (errorStatus === 401) {
        logger.warn(`⚠️ JobId ${jobId} unauthorized (status: ${errorStatus}). Clearing and starting fresh.`);
        setJobId(null);
        localStorage.removeItem(JOB_ID_STORAGE_KEY);
        localStorage.removeItem(TEMP_ACTIVE_JOB_ID_KEY);
        // Only remove metadata if unauthorized (different user)
        try {
          localStorage.removeItem(`${JOB_COURSE_DATA_STORAGE_KEY}-${jobId}`);
          logger.info(`🗑️ Removed metadata for unauthorized jobId: ${jobId}`);
        } catch (error) {
          logger.error("❌ Failed to remove metadata:", error);
        }
        // Remove jobId from URL
        const url = new URL(window.location.href);
        url.searchParams.delete("jobId");
        window.history.pushState({}, "", url.toString());
        // Navigate to first step
        goToStep(firstStep);
      } else if (errorStatus === 404) {
        // Job not found - but don't remove metadata yet
        // Job might still exist on server, just API hasn't loaded it yet
        // Or user might have closed tab and come back - job still exists
        logger.warn(`⚠️ JobId ${jobId} not found (404). Keeping metadata in case job exists on server.`);
        // Don't remove metadata - user might return later and job might exist
      }
    }
  }, [jobId, isJobStatusError, jobStatusError, goToStep]);

  // Load course data from result if job is completed and we haven't loaded it yet
  useEffect(() => {
    if (
      jobId &&
      jobStatus &&
      jobStatus.progressPercentage === 100 &&
      jobStatus.result &&
      Array.isArray(jobStatus.result) &&
      jobStatus.result.length > 0 &&
      !hasLoadedDataFromResult.current
    ) {
      hasLoadedDataFromResult.current = true;
      logger.info("📦 Loading course data from job result (progress = 100%)");
      
      try {
        // Transform API response to CourseData format
        const generatedCourseData = courseGenerationToCourse(jobStatus.result);
        const setDefaultSelectedTitle = (lesson: CourseData.Lesson) => {
          if (lesson.selectedTitle === undefined) {
            lesson.selectedTitle = 0;
          }
        };
        forEachLesson(generatedCourseData, setDefaultSelectedTitle);

        // Load course metadata (eBookAbout, targetAudience, conversationTone) from localStorage
        // This is needed because these fields are required when saving the course
        let courseMetadata: { eBookAbout?: string; targetAudience?: string; conversationTone?: string } = {};
        try {
          const storedData = localStorage.getItem(`${JOB_COURSE_DATA_STORAGE_KEY}-${jobId}`);
          if (storedData) {
            const parsed = JSON.parse(storedData);
            courseMetadata = {
              eBookAbout: parsed.eBookAbout,
              targetAudience: parsed.targetAudience,
              conversationTone: parsed.conversationTone,
            };
            logger.info("📦 Loaded course metadata from localStorage:", courseMetadata);
          } else {
            logger.warn("⚠️ No course metadata found in localStorage for jobId:", jobId);
          }
        } catch (error) {
          logger.error("❌ Failed to parse course metadata from localStorage:", error);
        }

        // Update course data with generated course AND metadata
        setCourseData((prev) => {
          const updated = {
            ...prev,
            ...courseMetadata, // Add metadata (eBookAbout, targetAudience, conversationTone)
            chapters: generatedCourseData.chapters ?? [],
            courseType: COURSE_TYPE.EBOOK,
          };
          logger.info("✅ Updated courseData with chapters and metadata:", {
            chaptersCount: updated.chapters?.length,
            hasMetadata: !!updated.eBookAbout && !!updated.targetAudience && !!updated.conversationTone,
          });
          return updated;
        });

        // Navigate to CreateCharacterStep after loading data
        // IMPORTANT: Don't clear jobId from localStorage here - we need it to identify which card to remove when saving
        // Only clear jobId from URL and state to hide progress step
        setJobId(null);
        const url = new URL(window.location.href);
        url.searchParams.delete("jobId");
        window.history.pushState({}, "", url.toString());
        // Note: JOB_ID_STORAGE_KEY and metadata remain in localStorage until course is saved

        // Navigate to step 1 (CreateCharacterStep) after jobId is cleared
        setTimeout(() => {
          logger.info("✅ Navigating to Create Character step");
          goToStep(1);
        }, 0);
      } catch (error) {
        logger.error("❌ Failed to load course data from result:", error);
        hasLoadedDataFromResult.current = false; // Reset on error to allow retry
      }
    }
  }, [jobId, jobStatus, goToStep]);

  // Auto-navigate to Create Character step when course data is ready
  useEffect(() => {
    const hasChapters = !!courseData?.chapters && courseData.chapters.length > 0;
    const isOnProgressStep = activeStep === progressStep;
    
    // If we're on progress step and course is generated, move to Create Character step
    if (hasChapters && isOnProgressStep && jobId) {
      logger.info("✅ Course data ready, moving to Create Character step");
      logger.info("✅ Chapters count:", courseData.chapters?.length);
      logger.info("✅ Current activeStep:", activeStep);
      
      // IMPORTANT: Clear jobId first, then navigate
      // After clearing jobId, CourseProgressStep is removed, so step indices change:
      // Before: 0=UploadEbook, 1=CourseProgress, 2=CreateCharacter
      // After:  0=UploadEbook, 1=CreateCharacter, 2=CourseReview
      // So we need to navigate to step 1 (CreateCharacterStep) after clearing jobId
      logger.info("✅ Clearing jobId first");
      setJobId(null);
      const url = new URL(window.location.href);
      url.searchParams.delete("jobId");
      window.history.pushState({}, "", url.toString());
      
      // Navigate to step 1 (CreateCharacterStep) after jobId is cleared
      // Use setTimeout to ensure state update completes first
      setTimeout(() => {
        logger.info("✅ Navigating to Create Character step (step 1 after clearing jobId)");
        goToStep(1); // CreateCharacterStep is now at index 1
      }, 0);
    }
  }, [courseData?.chapters, activeStep, jobId, goToStep]);

  // Check if course is generated (has chapters)
  const isCourseGenerated = !!courseData?.chapters && courseData.chapters.length > 0;

  return (
    <CourseEditorContextProvider courseData={courseData} setCourseData={setCourseData}>
      <ViewList {...stepperFlow} debug="MainFlow">
        {/* Course Info */}
        <ViewListItem>
          <UploadEbookStep onSubmit={onUploadCourse} isSubmitting={isStartingJob} />
        </ViewListItem>

        {/* Course Generation Progress */}
        {jobId && (
          <ViewListItem>
            <div className="mx-auto my-auto">
              <CourseProgressStep
                jobId={jobId}
                retry={() => {
                  // Clear jobId and retry
                  localStorage.removeItem(JOB_ID_STORAGE_KEY);
                  setJobId(null);
                  const url = new URL(window.location.href);
                  url.searchParams.delete("jobId");
                  window.history.pushState({}, "", url.toString());
                  if (prevValue) {
                    onUploadCourse(prevValue);
                  }
                }}
                onComplete={onCourseComplete}
              />
            </div>
          </ViewListItem>
        )}

        {/* Create Character */}
        <ViewListItem>
          <CreateCharacterStep />
        </ViewListItem>

        {/* Course Review */}
        <ViewListItem>
          {courseData && <CourseReviewStep saveCourseMutation={saveCourseMutation} prevValue={prevValue} />}
        </ViewListItem>

        {/* Course Created */}
        <ViewListItem>
          <CourseCreatedStep courseId={courseId} />,
        </ViewListItem>
      </ViewList>
    </CourseEditorContextProvider>
  );
}
